/**
 * Scenario loading utilities
 *
 * This module provides a centralized way to load and reset scenarios,
 * ensuring all necessary cleanup and initialization happens in the correct order.
 *
 * CRITICAL ORDER OF OPERATIONS:
 * When switching scenarios, you MUST follow this sequence:
 *
 * 1. clearProject() - Clears localStorage (if doing full reset)
 * 2. resetMap() - Resets map GeoJSON data (streets/buildings)
 * 3. loadScenarioMeta() - Load metadata FIRST (sets scenarioMeta.startTime)
 * 4. resetAnimation() - Clears markers AND resets clock to scenarioMeta.startTime
 * 5. loadAgents() - Load new agent data
 * 6. loadClockAnnotations() - Load new event annotations
 *
 * WHY THIS ORDER MATTERS:
 * - loadScenarioMeta() MUST be called BEFORE resetAnimation() because:
 *   - resetAnimation() reads scenarioMeta.startTime to reset the clock
 *   - If metadata isn't loaded first, clock resets to old scenario's start time
 *
 * - resetAnimation() MUST be called BEFORE loadAgents() because:
 *   - It clears waypoint markers (edit mode) via callback to clearAllWaypoints()
 *   - It clears simulation markers via callback to clearAllAgentMarkers()
 *   - If you call loadAgents() first, MapView's createEffect will try to draw
 *     waypoints for the new agents before the old waypoints are cleared,
 *     causing waypoint markers from the previous scenario to persist on the map
 *
 * USAGE:
 * - Use loadDefaultScenario() for loading the read-only default scenario
 * - Use loadCustomScenario() for forking default or loading a custom scenario
 * - Use createBlankScenario() for starting from scratch
 * - Use updateCurrentScenario() for clone-current (keeps agents, updates meta)
 * - Use loadScenarioFromProject() for URL/shared scenarios (simpler, no reset needed)
 */

import emptyScenario from "../data/scenario/empty-scenario.json"
import { resetMap } from "../stores/map"
import { clearProject } from "../stores/persistence"
import type { ScenarioActions } from "../stores/scenario/types"
import type { Agent, Analysis, Project, ScenarioMeta, SpeedControl, ViewportPin } from "../types"
import { resetAnimation } from "./animation"
import { getUserUUID } from "./uuid"

export interface ScenarioData {
    agents: Agent[]
    clockAnnotations?: any[]
    speedControls?: SpeedControl[]
    analysis?: Analysis
    meta?: ScenarioMeta
    viewportPins?: ViewportPin[]
    autoPan?: boolean
}

export type ScenarioResetMode = "full" | "keep-agents"

/**
 * Resets the canvas and loads a new scenario.
 *
 * Call this before loading any scenario to ensure clean state.
 * Order is critical:
 * 1. Clear localStorage (clearProject)
 * 2. Reset map GeoJSON data (resetMap)
 * 3. Load metadata (so scenarioMeta.startTime is set)
 * 4. Clear all markers via animation reset (resetAnimation - uses scenarioMeta.startTime)
 * 5. Load agents and annotations
 *
 * @param scenarioActions - The scenario store actions from useScenarioStore()
 * @param mode - 'full' clears everything, 'keep-agents' only resets animation/map
 * @param meta - Optional metadata to load before resetting animation (for correct clock start time)
 */
export function resetCanvas(scenarioActions: ScenarioActions, mode: ScenarioResetMode = "full", meta?: ScenarioMeta) {
    if (mode === "full") {
        clearProject()
    }
    resetMap()

    // Load metadata first if provided (so resetAnimation gets correct start time)
    if (meta) {
        scenarioActions.updateMeta(meta)
    }

    resetAnimation() // CRITICAL: Must happen after metadata but before loadAgents
}

/**
 * Complete workflow: Reset canvas and load default scenario
 *
 * @param scenarioActions - The scenario store actions from useScenarioStore()
 * @param defaultScenario - The default scenario data
 */
export function loadDefaultScenario(scenarioActions: ScenarioActions, defaultScenario: ScenarioData) {
    const meta = defaultScenario.meta || {
        version: 1,
        name: "Default Scenario",
        startTime: { hour: 23, minute: 6, second: 30 },
        crimeTime: { hour: 23, minute: 21, second: 30 },
        soundEffects: true,
    }

    resetCanvas(scenarioActions, "full", meta)

    scenarioActions.loadScenario({
        agents: defaultScenario.agents,
        clockAnnotations: defaultScenario.clockAnnotations || [],
        speedControls: defaultScenario.speedControls || [],
        analysis: defaultScenario.analysis ?? { scenarioName: "", ingress: "", sections: [] },
        viewportPins: defaultScenario.viewportPins || [],
        autoPan: defaultScenario.autoPan ?? true,
        meta,
    })

    // Sync analysis scenario name with meta
    scenarioActions.setAnalysisScenarioName(meta.name)
}

/**
 * Complete workflow: Reset canvas and load custom scenario
 *
 * @param scenarioActions - The scenario store actions from useScenarioStore()
 * @param scenario - The scenario data to load
 * @param metaOverrides - Optional overrides for scenario metadata
 */
export function loadCustomScenario(
    scenarioActions: ScenarioActions,
    scenario: ScenarioData,
    metaOverrides?: Partial<ScenarioMeta>
) {
    // Merge scenario meta with overrides
    const baseMeta = scenario.meta || {
        version: 1,
        name: "Scenario",
        startTime: { hour: 23, minute: 6, second: 30 },
        crimeTime: { hour: 23, minute: 21, second: 30 },
        soundEffects: true,
    }

    // Get current timestamp
    const now = new Date().toISOString()

    // Merge meta with overrides and add ownership/timestamp fields
    const finalMeta = {
        ...baseMeta,
        ...metaOverrides,
        ownerUUID: getUserUUID(),
        createdAt: metaOverrides?.createdAt || baseMeta.createdAt || now,
        updatedAt: now,
        slug: undefined, // Remove slug when forking (it's a new scenario)
    }

    resetCanvas(scenarioActions, "full", finalMeta)

    scenarioActions.loadScenario({
        agents: scenario.agents,
        clockAnnotations: scenario.clockAnnotations || [],
        speedControls: scenario.speedControls || [],
        analysis: scenario.analysis,
        viewportPins: scenario.viewportPins || [],
        autoPan: scenario.autoPan ?? true,
        meta: finalMeta,
    })

    // Sync analysis scenario name with meta
    scenarioActions.setAnalysisScenarioName(finalMeta.name)
}

/**
 * Complete workflow: Reset canvas and create blank scenario
 * Loads the empty scenario template with basic agents and settings
 *
 * @param scenarioActions - The scenario store actions from useScenarioStore()
 * @param metaOverrides - Optional overrides for scenario metadata
 */
export function createBlankScenario(scenarioActions: ScenarioActions, metaOverrides?: Partial<ScenarioMeta>) {
    // Use the empty scenario template as base
    const baseProject = emptyScenario as any

    // Get current timestamp
    const now = new Date().toISOString()

    // Merge meta with overrides and add ownership/timestamp fields
    const finalMeta = {
        ...baseProject.meta,
        ...metaOverrides,
        ownerUUID: getUserUUID(),
        createdAt: now,
        updatedAt: now,
    }

    resetCanvas(scenarioActions, "full", finalMeta)

    // Load the template data
    scenarioActions.loadScenario({
        agents: baseProject.agents || [],
        clockAnnotations: baseProject.clockAnnotations || [],
        speedControls: baseProject.speedControls || [],
        analysis: baseProject.analysis,
        viewportPins: baseProject.viewportPins || [],
        autoPan: baseProject.autoPan ?? true,
        meta: finalMeta,
    })

    // Sync analysis scenario name with meta
    scenarioActions.setAnalysisScenarioName(finalMeta.name)
}

/**
 * Update scenario without full reset (for clone-current workflow)
 *
 * @param scenarioActions - The scenario store actions from useScenarioStore()
 * @param metaOverrides - Metadata updates to apply
 */
export function updateCurrentScenario(scenarioActions: ScenarioActions, metaOverrides: Partial<ScenarioMeta>) {
    resetCanvas(scenarioActions, "keep-agents")

    // Get current timestamp and add ownership
    const now = new Date().toISOString()
    const finalMeta = {
        ...metaOverrides,
        ownerUUID: getUserUUID(),
        createdAt: metaOverrides.createdAt || now,
        updatedAt: now,
        slug: undefined, // Remove slug when cloning (it's a new scenario)
    }

    scenarioActions.updateMeta(finalMeta)

    // Sync analysis scenario name with meta
    scenarioActions.setAnalysisScenarioName(finalMeta.name || "Importerat scenario")

    scenarioActions.saveToLocalStorage()
}

/**
 * Load a scenario from a Project object (used for shared/URL scenarios).
 * This is simpler than loadScenario because Project already has the right structure.
 * Includes migration for old projects without viewportPins field.
 *
 * @param scenarioActions - The scenario store actions from useScenarioStore()
 * @param project - The project data to load
 */
export function loadScenarioFromProject(
    scenarioActions: ScenarioActions,
    project: Project | (Omit<Project, "viewportPins"> & { viewportPins?: ViewportPin[] })
) {
    // Migrate old projects without viewportPins field
    const viewportPins = project.viewportPins || [
        {
            id: "viewport-first",
            time: "default-start" as const,
            position: [59.33914127606005, 18.059999942779545] as [number, number],
            zoom: 18 as const,
        },
    ]

    const autoPan = project.autoPan !== undefined ? project.autoPan : true

    scenarioActions.loadScenario({
        agents: project.agents,
        clockAnnotations: project.clockAnnotations || [],
        speedControls: project.speedControls || [],
        analysis: project.analysis,
        viewportPins,
        autoPan,
        meta: project.meta,
    })

    // Sync analysis scenario name with meta
    scenarioActions.setAnalysisScenarioName(project.meta.name)
}
